import styled from 'styled-components';
import {
  addMilliseconds,
  differenceInSeconds,
  formatDuration,
  intervalToDuration,
  isAfter,
} from 'date-fns';
import { round } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { usePositionsView } from '@paradigm/features/src/positions/contexts/store';
import {
  bigStone,
  blackTangaroa,
  linkWater,
  raven,
  ripeLemon,
} from '@paradigm/design-system/src/colors';
import { Nullable } from '@paradigm/utils/types';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import { default as BaseTimer } from '#/components/UnifiedRfqs/components/Timer';
import { usePositionsSubscriberStatus } from '@paradigm/features/src/positions/contexts/subscriber';
import { usePositionsFetcherState } from '@paradigm/features/src/positions/contexts/fetcher';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

const TIMEOUT_MS = 120000;

/*
 * We should show this component after timeout (2min) has elapsed, and we haven't received any
 * updates from websocket channel by checking `lastUpdatedAt` from payload.
 */
function FailedToFetch({ formattedDuration }: { formattedDuration: string }) {
  return (
    <>
      <Text data-testid="positions-table-last-updated">
        <Hightlight color={ripeLemon}>
          Failed to fetch Positions. Retrying…
        </Hightlight>
        {formattedDuration}
      </Text>
      <LoadingSpinner size={20} />
    </>
  );
}

function UpdatingPositions({
  elapsedTime,
  formattedDuration,
}: {
  elapsedTime: number;
  formattedDuration: string;
}) {
  return (
    <>
      <Text
        css="grid-area: text"
        data-testid="positions-table-last-updated-successful"
      >
        <Hightlight color={linkWater}>Updating…</Hightlight>
        {formattedDuration}
      </Text>
      <Timer css="grid-area: timer" elapsed={elapsedTime} />
    </>
  );
}

/**
 * Timer component that shows how much time has elapsed since last update.
 * @returns JSX.Element
 */
function Timer({ elapsed }: { elapsed: number }) {
  const durationInSeconds = TIMEOUT_MS / 1000;

  return <BaseTimer duration={durationInSeconds} elapsed={elapsed} />;
}

function useElapsedTime(lastUpdatedAt: Nullable<Date>): number {
  const [elapsed, setElapsed] = useState<Nullable<number>>(null);

  useEffect(() => {
    if (lastUpdatedAt == null) return;
    setElapsed(differenceInSeconds(Date.now(), lastUpdatedAt));
    const intervalId = setInterval(() => {
      setElapsed(differenceInSeconds(Date.now(), lastUpdatedAt));
    }, 2000);
    return () => clearInterval(intervalId);
  }, [lastUpdatedAt]);

  return elapsed ?? 0;
}

function useFailedToFetch(
  lastUpdatedAt: Nullable<Date>,
  isConnectionError: boolean,
): boolean {
  const [hasFailedToFetch, setHasFailedToFetch] = useState<boolean>(false);

  useEffect(() => {
    if (lastUpdatedAt == null) return;
    setHasFailedToFetch(
      isConnectionError ||
        isAfter(Date.now(), addMilliseconds(lastUpdatedAt, TIMEOUT_MS)),
    );
    const intervalId = setInterval(() => {
      setHasFailedToFetch(
        isConnectionError ||
          isAfter(Date.now(), addMilliseconds(lastUpdatedAt, TIMEOUT_MS)),
      );
    }, 1000);
    return () => clearInterval(intervalId);
  }, [lastUpdatedAt, isConnectionError, setHasFailedToFetch]);

  return hasFailedToFetch;
}

export default function LastUpdatedAt() {
  const { lastUpdatedAt } = usePositionsView();
  const { status: subscriberStatus } = usePositionsSubscriberStatus();
  const { status: fetcherStatus } = usePositionsFetcherState();

  const hasConnectionError =
    subscriberStatus === 'ERROR' || fetcherStatus === 'ERROR';
  const hasFailedToFetch = useFailedToFetch(lastUpdatedAt, hasConnectionError);

  const elapsedTimeInSeconds = useElapsedTime(lastUpdatedAt);
  const elapsedTimeRounded = round(elapsedTimeInSeconds, -1);

  const formattedDuration = useMemo(
    () =>
      formatDuration(
        // transforming to intervals acceps only milliseconds
        intervalToDuration({ start: 0, end: elapsedTimeRounded * 1000 }),
      ),
    [elapsedTimeRounded],
  );

  if (lastUpdatedAt === null) return null;

  return (
    <Container>
      {hasFailedToFetch ? (
        <FailedToFetch
          formattedDuration={getDurationDescription(formattedDuration)}
        />
      ) : (
        <UpdatingPositions
          elapsedTime={elapsedTimeInSeconds}
          formattedDuration={getDurationDescription(formattedDuration)}
        />
      )}
    </Container>
  );
}

function getDurationDescription(formattedDuration: string) {
  return formattedDuration === ''
    ? 'Just updated'
    : `last updated ${formattedDuration} ago`;
}

const Text = styled.div`
  color: ${raven};
  font-size: 11px;
  font-weight: 500;
  line-height: 12px;
  text-align: right;
`;

const Hightlight = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 12px;
  margin-bottom: 2px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: initial;
  }
`;

const Container = styled.div`
  display: grid;
  grid-area: last;
  justify-self: flex-end;
  grid-template-areas: 'text timer';
  grid-template-columns: auto auto;
  gap: 10px;
  align-items: center;
  svg {
    margin-left: 6px;
    width: 14px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    height: 100%;
    justify-content: start;
    padding: 0.5rem;
    gap: 6px;
    justify-self: flex-start;
    grid-template-areas: 'timer text';
    background-color: ${blackTangaroa};
    border-top: 1px solid ${bigStone};
  }
`;
